import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Copy, DollarSign, Users, TrendingUp, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { PerformanceDashboard } from "@/components/affiliate/PerformanceDashboard";
import { MarketingMaterialsLibrary } from "@/components/affiliate/MarketingMaterialsLibrary";
import { TrackingLinkGenerator } from "@/components/affiliate/TrackingLinkGenerator";
import { TeamManagement } from "@/components/affiliate/TeamManagement";

interface ReferralStats {
  total_referrals: number;
  active_referrals: number;
  total_earnings: number;
  pending_earnings: number;
  paid_earnings: number;
}

interface Referral {
  id: string;
  referred_user_id: string;
  status: string;
  signup_date: string;
  purchase_date: string | null;
  purchase_amount: number;
  commission_earned: number;
  profiles?: { username: string };
}

interface Payout {
  id: string;
  amount: number;
  status: string;
  requested_at: string;
  approved_at: string | null;
  paid_at: string | null;
  payment_method: string | null;
}

const DashboardReferrals = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [referralCode, setReferralCode] = useState("");
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentDetails, setPaymentDetails] = useState("");
  const [requestingPayout, setRequestingPayout] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      // Load or create referral code
      let { data: codeData } = await supabase
        .from("referral_codes")
        .select("code")
        .eq("user_id", user.id)
        .single();

      if (!codeData) {
        const newCode = `REF${Math.random().toString(36).substring(2, 10).toUpperCase()}`;
        const { data: newCodeData } = await supabase
          .from("referral_codes")
          .insert({ user_id: user.id, code: newCode })
          .select("code")
          .single();
        codeData = newCodeData;
      }

      setReferralCode(codeData?.code || "");

      // Load stats
      let { data: statsData } = await supabase
        .from("referral_stats")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (!statsData) {
        const { data: newStats } = await supabase
          .from("referral_stats")
          .insert({ user_id: user.id })
          .select()
          .single();
        statsData = newStats;
      }

      setStats(statsData);

      // Load referrals with profiles
      const { data: referralsData } = await supabase
        .from("referrals")
        .select("*")
        .eq("referrer_id", user.id)
        .order("created_at", { ascending: false });

      if (referralsData && referralsData.length > 0) {
        const referredIds = [...new Set(referralsData.map(r => r.referred_user_id))];
        const { data: profilesData } = await supabase
          .from("profiles")
          .select("id, username")
          .in("id", referredIds);

        const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || []);
        const enrichedReferrals = referralsData.map(r => ({
          ...r,
          profiles: profilesMap.get(r.referred_user_id),
        }));

        setReferrals(enrichedReferrals as any);
      } else {
        setReferrals([]);
      }

      // Load payouts
      const { data: payoutsData } = await supabase
        .from("payouts")
        .select("*")
        .eq("user_id", user.id)
        .order("requested_at", { ascending: false });

      setPayouts(payoutsData || []);
    } catch (error: any) {
      toast.error("Failed to load referral data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/auth?ref=${referralCode}`;
    navigator.clipboard.writeText(link);
    toast.success("Referral link copied!");
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success("Referral code copied!");
  };

  const requestPayout = async () => {
    if (!payoutAmount || !paymentMethod || !paymentDetails) {
      toast.error("Please fill in all fields");
      return;
    }

    const amount = parseInt(payoutAmount);
    if (amount < 5000) {
      toast.error("Minimum payout is $50.00");
      return;
    }

    if (stats && amount > stats.pending_earnings) {
      toast.error("Insufficient pending earnings");
      return;
    }

    setRequestingPayout(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from("payouts").insert({
        user_id: user.id,
        amount,
        payment_method: paymentMethod,
        payment_details: { details: paymentDetails },
      });

      toast.success("Payout request submitted!");
      setPayoutAmount("");
      setPaymentMethod("");
      setPaymentDetails("");
      loadData();
    } catch (error) {
      toast.error("Failed to request payout");
      console.error(error);
    } finally {
      setRequestingPayout(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      pending: "secondary",
      active: "default",
      completed: "default",
      approved: "default",
      paid: "default",
    };
    return <Badge variant={variants[status] || "secondary"}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-4xl font-bold flex items-center gap-2">
                <Sparkles className="w-8 h-8 text-primary" />
                Affiliate Portal
              </h1>
              <p className="text-muted-foreground mt-1">
                Advanced referral system with tiered commissions and team bonuses
              </p>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="border-2 border-primary/20 bg-gradient-to-br from-card to-primary/5">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Referrals</CardTitle>
              <Users className="w-4 h-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats?.total_referrals || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {stats?.active_referrals || 0} active conversions
              </p>
            </CardContent>
          </Card>

          <Card className="border-2 border-green-500/20 bg-gradient-to-br from-card to-green-500/5">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
              <DollarSign className="w-4 h-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                ${((stats?.total_earnings || 0) / 100).toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Lifetime commission earned
              </p>
            </CardContent>
          </Card>

          <Card className="border-2 border-blue-500/20 bg-gradient-to-br from-card to-blue-500/5">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Available Balance</CardTitle>
              <TrendingUp className="w-4 h-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">
                ${((stats?.pending_earnings || 0) / 100).toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Ready to withdraw
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="dashboard" className="space-y-6">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="links">Tracking Links</TabsTrigger>
            <TabsTrigger value="materials">Marketing</TabsTrigger>
            <TabsTrigger value="team">Team</TabsTrigger>
            <TabsTrigger value="referrals">Referrals</TabsTrigger>
            <TabsTrigger value="payouts">Payouts</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Your Referral Code</CardTitle>
                <CardDescription>Share your unique code to start earning</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input value={referralCode} readOnly className="font-mono text-lg" />
                  <Button onClick={copyReferralCode} size="lg">
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Code
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={`${window.location.origin}/auth?ref=${referralCode}`}
                    readOnly
                    className="text-sm"
                  />
                  <Button onClick={copyReferralLink} variant="outline" size="lg">
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Link
                  </Button>
                </div>
              </CardContent>
            </Card>

            <PerformanceDashboard
              activeReferrals={stats?.active_referrals || 0}
              totalEarnings={stats?.total_earnings || 0}
              pendingEarnings={stats?.pending_earnings || 0}
            />
          </TabsContent>

          {/* Tracking Links Tab */}
          <TabsContent value="links">
            <TrackingLinkGenerator referralCode={referralCode} />
          </TabsContent>

          {/* Marketing Materials Tab */}
          <TabsContent value="materials">
            <MarketingMaterialsLibrary />
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team">
            <TeamManagement />
          </TabsContent>

          {/* Referrals Tab */}
          <TabsContent value="referrals">
            <Card>
              <CardHeader>
                <CardTitle>Your Referrals</CardTitle>
                <CardDescription>Track all your referred users and earnings</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Signup Date</TableHead>
                      <TableHead>Purchase</TableHead>
                      <TableHead>Commission</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {referrals.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          No referrals yet. Share your link to get started!
                        </TableCell>
                      </TableRow>
                    ) : (
                      referrals.map((referral) => (
                        <TableRow key={referral.id}>
                          <TableCell className="font-medium">
                            {referral.profiles?.username || "User"}
                          </TableCell>
                          <TableCell>{getStatusBadge(referral.status)}</TableCell>
                          <TableCell>
                            {new Date(referral.signup_date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {referral.purchase_amount
                              ? `$${(referral.purchase_amount / 100).toFixed(2)}`
                              : "-"}
                          </TableCell>
                          <TableCell className="font-semibold text-green-600">
                            ${(referral.commission_earned / 100).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payouts Tab */}
          <TabsContent value="payouts" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Request Payout</CardTitle>
                <CardDescription>
                  Minimum payout: $50.00 | Available: ${((stats?.pending_earnings || 0) / 100).toFixed(2)}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button size="lg" disabled={(stats?.pending_earnings || 0) < 5000}>
                      <DollarSign className="w-4 h-4 mr-2" />
                      Request Payout
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Request Payout</DialogTitle>
                      <DialogDescription>
                        Enter your payment details to request a payout
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="amount">Amount (in cents)</Label>
                        <Input
                          id="amount"
                          type="number"
                          placeholder="5000 = $50.00"
                          value={payoutAmount}
                          onChange={(e) => setPayoutAmount(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="method">Payment Method</Label>
                        <Input
                          id="method"
                          placeholder="PayPal, Bank Transfer, Wise, etc."
                          value={paymentMethod}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="details">Payment Details</Label>
                        <Input
                          id="details"
                          placeholder="Email, account number, etc."
                          value={paymentDetails}
                          onChange={(e) => setPaymentDetails(e.target.value)}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button onClick={requestPayout} disabled={requestingPayout}>
                        {requestingPayout ? "Submitting..." : "Submit Request"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Payout History</CardTitle>
                <CardDescription>Track your payout requests and payments</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Requested</TableHead>
                      <TableHead>Paid</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payouts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          No payout requests yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      payouts.map((payout) => (
                        <TableRow key={payout.id}>
                          <TableCell className="font-semibold">
                            ${(payout.amount / 100).toFixed(2)}
                          </TableCell>
                          <TableCell>{getStatusBadge(payout.status)}</TableCell>
                          <TableCell>{payout.payment_method || "-"}</TableCell>
                          <TableCell>
                            {new Date(payout.requested_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {payout.paid_at
                              ? new Date(payout.paid_at).toLocaleDateString()
                              : "-"}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DashboardReferrals;
